import { useState, useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { TournamentBracket } from "@/components/TournamentBracket";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, Trophy, Edit, UserPlus, Trash2, FileDown, Clock } from "lucide-react";
import { exportBracketPDF } from "@/lib/pdfExport";

const ROUND_LABELS = {
  R256: "Turno Preliminare", R128: "128-esimi", R64: "64-esimi", R32: "32-esimi",
  R16: "16-esimi", QF: "Quarti di Finale", SF: "Semifinale", F: "Finale",
};

export default function BracketView() {
  const { id, catId } = useParams();
  const [drawData, setDrawData] = useState(null);
  const [tournament, setTournament] = useState(null);
  const [players, setPlayers] = useState([]);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [dialogMode, setDialogMode] = useState("result");
  const [winnerId, setWinnerId] = useState("");
  const [score, setScore] = useState("");
  const [swapPosition, setSwapPosition] = useState("player1");
  const [swapPlayerId, setSwapPlayerId] = useState("");
  const [loading, setLoading] = useState(true);
  const [editingSlot, setEditingSlot] = useState(null);
  const [selectedPlayerId, setSelectedPlayerId] = useState("");
  const [clearing, setClearing] = useState(false);
  const [venue, setVenue] = useState(null);
  const [schedDate, setSchedDate] = useState("");
  const [schedTime, setSchedTime] = useState("");
  const [schedCourt, setSchedCourt] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [scheduling, setScheduling] = useState(false);
  const [allMatches, setAllMatches] = useState([]);

  const fetchData = async () => {
    try {
      const [t, draw, pl] = await Promise.all([
        api.get(`/tournaments/${id}`),
        api.get(`/tournaments/${id}/categories/${catId}/draw`),
        api.get(`/tournaments/${id}/categories/${catId}/players`),
      ]);
      setTournament(t);
      setDrawData(draw);
      setPlayers(pl);
      // Load all matches for conflict detection
      try {
        const sched = await api.get(`/tournaments/${id}/schedule`);
        setAllMatches(sched.matches || []);
      } catch { setAllMatches([]); }
    } catch (e) {
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    api.get(`/tournaments/${id}/venues`).then((v) => setVenue(v?.[0] || null)).catch(() => {});
  }, [id, catId]); // eslint-disable-line

  const category = tournament?.categories?.find((c) => c.category_id === catId);

  const submitResult = async () => {
    if (!winnerId) { toast.error("Seleziona il vincitore"); return; }
    try {
      await api.put(`/tournaments/${id}/matches/${selectedMatch.match_id}`, { winner_id: winnerId, score });
      // Save start_time and end_time if provided
      if (startTime || endTime) {
        const timePayload = {};
        if (startTime) timePayload.start_time = startTime;
        if (endTime) timePayload.end_time = endTime;
        await api.put(`/tournaments/${id}/matches/${selectedMatch.match_id}/reschedule`, timePayload);
      }
      toast.success("Risultato registrato!");
      setSelectedMatch(null); setWinnerId(""); setScore(""); fetchData();
    } catch (e) { toast.error("Errore"); }
  };

  const submitSwap = async () => {
    if (!swapPlayerId) { toast.error("Seleziona un giocatore"); return; }
    try {
      await api.put(`/tournaments/${id}/matches/${selectedMatch.match_id}/swap`, {
        match_id: selectedMatch.match_id, position: swapPosition, new_player_id: swapPlayerId,
      });
      toast.success("Giocatore assegnato!");
      setSelectedMatch(null); setSwapPlayerId(""); fetchData();
    } catch (e) { toast.error("Errore"); }
  };

  const handleMatchClick = (match) => {
    setSelectedMatch(match);
    setWinnerId(""); setScore(""); setSwapPlayerId("");
    setSchedDate(match.scheduled_date || "");
    setSchedTime(match.scheduled_time || "");
    setSchedCourt(match.scheduled_court != null ? String(match.scheduled_court) : "");
    setStartTime(match.start_time || "");
    setEndTime(match.end_time || "");
    const p1Real = match.player1_id && match.player1_id !== "BYE";
    const p2Real = match.player2_id && match.player2_id !== "BYE";
    setDialogMode(p1Real && p2Real ? "result" : "schedule");
  };

  // Build conflict map: key = "date|time|court" -> count of matches
  const conflictSet = useMemo(() => {
    const counts = {};
    for (const m of allMatches) {
      if (m.scheduled_date && m.scheduled_time && m.scheduled_court != null) {
        const key = `${m.scheduled_date}|${m.scheduled_time}|${m.scheduled_court}`;
        counts[key] = (counts[key] || 0) + 1;
      }
    }
    const conflicts = new Set();
    for (const [key, count] of Object.entries(counts)) {
      if (count > 1) conflicts.add(key);
    }
    return conflicts;
  }, [allMatches]);

  const submitSchedule = async () => {
    if (!schedDate && !schedTime && !schedCourt && !startTime && !endTime) {
      toast.error("Inserisci almeno un campo"); return;
    }
    setScheduling(true);
    try {
      const payload = {};
      if (schedDate) payload.scheduled_date = schedDate;
      if (schedTime) payload.scheduled_time = schedTime;
      if (schedCourt) payload.scheduled_court = parseInt(schedCourt, 10);
      if (startTime) payload.start_time = startTime;
      if (endTime) payload.end_time = endTime;
      await api.put(`/tournaments/${id}/matches/${selectedMatch.match_id}/reschedule`, payload);
      toast.success("Programmazione aggiornata!");
      setSelectedMatch(null);
      fetchData();
    } catch (e) { toast.error(e.message || "Errore"); } finally { setScheduling(false); }
  };

  const handleEditPlayerName = ({ playerId, currentName, matchId, position }) => {
    setEditingSlot({ matchId, position, currentPlayerId: playerId, currentName });
    setSelectedPlayerId("");
  };

  const savePlayerSwap = async () => {
    if (!selectedPlayerId) { toast.error("Seleziona un giocatore"); return; }
    try {
      await api.put(`/tournaments/${id}/matches/${editingSlot.matchId}/swap`, {
        match_id: editingSlot.matchId,
        position: editingSlot.position,
        new_player_id: selectedPlayerId,
      });
      toast.success(selectedPlayerId === "BYE" ? "BYE assegnato!" : selectedPlayerId === "TBD" ? "Impostato come TBD" : "Giocatore assegnato!");
      setEditingSlot(null);
      setSelectedPlayerId("");
      fetchData();
    } catch (e) { toast.error("Errore nell'assegnazione"); }
  };

  const clearBracket = async () => {
    setClearing(true);
    try {
      await api.del(`/tournaments/${id}/categories/${catId}/draw`);
      toast.success("Tabellone svuotato");
      fetchData();
    } catch (e) { toast.error("Errore"); }
    finally { setClearing(false); }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><div className="animate-spin h-8 w-8 border-4 border-green-700 border-t-transparent rounded-full" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="bracket-view-page">
      <div className="flex items-center gap-4">
        <Link to={`/tournament/${id}`}>
          <Button variant="outline" size="sm" className="rounded-sm gap-1"><ArrowLeft className="h-4 w-4" /> Indietro</Button>
        </Link>
        <div>
          <p className="text-xs text-slate-500 uppercase tracking-widest font-bold">Tabellone</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight text-slate-900">{category?.name || "Categoria"}</h1>
        </div>
        <div className="flex items-center gap-2 ml-auto">
          {drawData?.matches?.length > 0 && (
            <>
              <Badge variant="secondary" className="text-xs rounded-sm">
                <Trophy className="h-3 w-3 mr-1" />
                {drawData.matches.filter((m) => m.status === "completed").length} / {drawData.matches.filter((m) => m.player1_id && m.player2_id && m.status !== "bye").length} completate
              </Badge>
              <Button
                data-testid="clear-bracket-btn"
                variant="outline"
                size="sm"
                className="rounded-sm gap-1 text-red-600 hover:text-red-700 border-red-200 hover:border-red-300 hover:bg-red-50"
                onClick={clearBracket}
                disabled={clearing}
              >
                <Trash2 className="h-3.5 w-3.5" /> Svuota
              </Button>
              <Button
                data-testid="export-bracket-view-pdf-btn"
                variant="outline"
                size="sm"
                className="rounded-sm gap-1 text-green-700 border-green-200 hover:bg-green-50"
                onClick={() => exportBracketPDF(tournament, drawData, category?.name || "", venue)}
              >
                <FileDown className="h-3.5 w-3.5" /> PDF
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="bg-slate-100 p-3 rounded-sm text-sm text-slate-600 flex items-center gap-2">
        <Edit className="h-4 w-4" />
        <span>Passa il mouse su un giocatore per sostituirlo. Clicca sugli slot per assegnare giocatori, BYE o TBD.</span>
      </div>

      {drawData?.matches?.length > 0 ? (
        <div className="bg-white border border-slate-200 rounded-sm p-2">
          <TournamentBracket
            matches={drawData.matches}
            rounds={drawData.rounds}
            onMatchClick={handleMatchClick}
            onEditPlayerName={handleEditPlayerName}
            conflictSet={conflictSet}
          />
        </div>
      ) : (
        <div className="text-center py-20 text-slate-500">Nessun tabellone generato</div>
      )}

      {/* Match Dialog */}
      <Dialog open={!!selectedMatch} onOpenChange={() => setSelectedMatch(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase">
              {dialogMode === "result" ? "Inserisci Risultato" : dialogMode === "schedule" ? "Riprogramma Incontro" : "Modifica Giocatore"}
            </DialogTitle>
          </DialogHeader>
          {selectedMatch && (
            <div>
              <Tabs value={dialogMode} onValueChange={setDialogMode}>
                <TabsList className="w-full mb-4">
                  <TabsTrigger value="schedule" className="flex-1 rounded-sm gap-1 text-xs">
                    <Clock className="h-3 w-3" /> Programmazione
                  </TabsTrigger>
                  <TabsTrigger value="result" className="flex-1 rounded-sm gap-1 text-xs" disabled={
                    !selectedMatch.player1_id || !selectedMatch.player2_id ||
                    selectedMatch.player1_id === "BYE" || selectedMatch.player2_id === "BYE"
                  }>
                    <Trophy className="h-3 w-3" /> Risultato
                  </TabsTrigger>
                  <TabsTrigger value="swap" className="flex-1 rounded-sm gap-1 text-xs">
                    <UserPlus className="h-3 w-3" /> Modifica
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="result">
                  <div className="space-y-4">
                    <div className="bg-slate-50 rounded-sm p-4 text-center">
                      <p className="text-sm font-medium">{selectedMatch.player1_name || "TBD"} <span className="text-slate-400 mx-2">vs</span> {selectedMatch.player2_name || "TBD"}</p>
                      <p className="text-xs text-slate-500 mt-1">{ROUND_LABELS[selectedMatch.round] || selectedMatch.round}</p>
                      <Button data-testid="result-goto-modifica-btn" variant="outline" size="sm" onClick={() => setDialogMode("swap")} className="mt-2 text-xs rounded-sm gap-1 border-slate-300 hover:border-green-500 hover:bg-green-50">
                        <Edit className="h-3 w-3" /> Modifica
                      </Button>
                    </div>
                    <div>
                      <Label className="text-xs font-bold uppercase">Vincitore</Label>
                      <Select value={winnerId} onValueChange={setWinnerId}>
                        <SelectTrigger data-testid="winner-select" className="mt-1 rounded-sm"><SelectValue placeholder="Seleziona vincitore" /></SelectTrigger>
                        <SelectContent>
                          {selectedMatch.player1_id && selectedMatch.player1_id !== "BYE" && <SelectItem value={selectedMatch.player1_id}>{selectedMatch.player1_name}</SelectItem>}
                          {selectedMatch.player2_id && selectedMatch.player2_id !== "BYE" && <SelectItem value={selectedMatch.player2_id}>{selectedMatch.player2_name}</SelectItem>}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs font-bold uppercase">Punteggio</Label>
                      <Input data-testid="score-input" value={score} onChange={(e) => setScore(e.target.value)} placeholder="es. 6-4 6-2" className="mt-1 rounded-sm" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs font-bold uppercase">Ora Inizio <span className="text-slate-400 font-normal normal-case">(opzionale)</span></Label>
                        <Input data-testid="result-start-time" type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className="mt-1 rounded-sm" />
                      </div>
                      <div>
                        <Label className="text-xs font-bold uppercase">Ora Fine <span className="text-slate-400 font-normal normal-case">(opzionale)</span></Label>
                        <Input data-testid="result-end-time" type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} className="mt-1 rounded-sm" />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button data-testid="submit-result-btn" onClick={submitResult} className="flex-1 bg-green-700 hover:bg-green-800 text-white font-bold uppercase rounded-sm">Salva Risultato</Button>
                      <Button data-testid="open-reschedule-btn" variant="outline" onClick={() => setDialogMode("schedule")} className="border-amber-500 text-amber-600 hover:bg-amber-50 font-bold uppercase rounded-sm text-xs">
                        <Clock className="h-3.5 w-3.5 mr-1" /> Riprogramma
                      </Button>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="schedule">
                  <div className="space-y-3">
                    <div className="bg-slate-50 rounded-sm p-3 text-center text-sm">
                      <p className="font-medium">{selectedMatch.player1_name || "TBD"} <span className="text-slate-400 mx-2">vs</span> {selectedMatch.player2_name || "TBD"}</p>
                      <p className="text-xs text-slate-500 mt-1">{ROUND_LABELS[selectedMatch.round] || selectedMatch.round}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs font-bold uppercase">Data</Label>
                        <Input data-testid="bracket-sched-date" type="date" value={schedDate} onChange={(e) => setSchedDate(e.target.value)} className="mt-1 rounded-sm" />
                      </div>
                      <div>
                        <Label className="text-xs font-bold uppercase">Campo</Label>
                        <Input data-testid="bracket-sched-court" type="number" min="1" value={schedCourt} onChange={(e) => setSchedCourt(e.target.value)} placeholder="es. 1" className="mt-1 rounded-sm" />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs font-bold uppercase">Orario Programmato</Label>
                      <Input data-testid="bracket-sched-time" type="time" value={schedTime} onChange={(e) => setSchedTime(e.target.value)} className="mt-1 rounded-sm" />
                    </div>
                    <Button data-testid="bracket-sched-submit" onClick={submitSchedule} disabled={scheduling} className="w-full bg-amber-600 hover:bg-amber-700 text-white font-bold uppercase rounded-sm">
                      {scheduling ? "Salvando..." : "Salva Programmazione"}
                    </Button>
                  </div>
                </TabsContent>


                <TabsContent value="swap">
                  <div className="space-y-4">
                    <div className="bg-slate-50 rounded-sm p-4 text-center text-sm">
                      <p>Partita: <strong>{selectedMatch.player1_name || "TBD"}</strong> vs <strong>{selectedMatch.player2_name || "TBD"}</strong></p>
                    </div>
                    <div>
                      <Label className="text-xs font-bold uppercase">Giocatore 1</Label>
                      <Select value={swapPosition} onValueChange={setSwapPosition}>
                        <SelectTrigger className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="player1">{selectedMatch.player1_name || "vuoto"}</SelectItem>
                          <SelectItem value="player2">{selectedMatch.player2_name || "vuoto"}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs font-bold uppercase">Giocatore 2</Label>
                      <Select value={swapPlayerId} onValueChange={setSwapPlayerId}>
                        <SelectTrigger data-testid="swap-player-select" className="mt-1 rounded-sm"><SelectValue placeholder="Seleziona giocatore" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="BYE" className="text-slate-400 italic">BYE (salta turno)</SelectItem>
                          <SelectItem value="TBD" className="text-amber-500">TBD (da definire)</SelectItem>
                          {players.map(p => <SelectItem key={p.player_id} value={p.player_id}>{p.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button data-testid="submit-swap-btn" onClick={submitSwap} className="w-full bg-green-700 hover:bg-green-800 text-white font-bold uppercase rounded-sm">Assegna Giocatore</Button>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Player from Category Dialog */}
      <Dialog open={!!editingSlot} onOpenChange={() => { setEditingSlot(null); setSelectedPlayerId(""); }}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase">
              {editingSlot?.currentPlayerId ? "Sostituisci Giocatore" : "Assegna Giocatore"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            {editingSlot?.currentName && (
              <div className="bg-slate-50 rounded-sm p-3 text-center text-sm">
                <p className="text-slate-500">Giocatore attuale:</p>
                <p className="font-medium">{editingSlot.currentName}</p>
              </div>
            )}
            <div>
              <Label className="text-xs font-bold uppercase">Seleziona Giocatore</Label>
              <Select value={selectedPlayerId} onValueChange={setSelectedPlayerId}>
                <SelectTrigger data-testid="edit-select-player" className="mt-1 rounded-sm">
                  <SelectValue placeholder="Scegli dalla lista..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="BYE" className="text-slate-400 italic">BYE (salta turno)</SelectItem>
                  <SelectItem value="TBD" className="text-amber-500">TBD (da definire)</SelectItem>
                  {players.map(p => (
                    <SelectItem key={p.player_id} value={p.player_id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              data-testid="save-player-swap-btn"
              onClick={savePlayerSwap}
              className="w-full bg-green-700 hover:bg-green-800 text-white font-bold uppercase rounded-sm"
            >
              Assegna Giocatore
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
